import { Arn } from 'src/utils/types'
import { ResourceType, ResourceVariant } from 'src/utils/resource-types'

// DTO objects for the Arpio API

export interface UserLoginDto {
  email: string
  password: string
}

export interface UserAuthenticationResponseDto {
  nativeAuthToken: string
}

export interface UserDto {
  email: string
  user?: string
  password?: string
}

export interface GetUserResponseDto {
  userId: string
  email: string
  name: string
  adminRole?: string
  password?: string // This field is present, but pretty sure it should never actually be returned!
}

export interface NativeAcsRequestDto {
  authToken: string
  nativeAuthToken: string
}

export interface NativeAcsResponseDto {
  redirectUrl: string
}

export interface AuthenticateResponse {
  loginUrl: string
}

export interface AccountDto {
  accountId?: string
  name: string
  createdAt?: Date
  entitlementExpiration?: Date
  stacksetsEnabled?: boolean | null
}

export interface CreateAccountRequestDto {
  nativeAuthToken: string
  account: AccountDto
}

export interface AccountEntitlementTokenDto {
  entitlementToken: string
}

export interface EntitlementDto {
  expiration: Date
  productName: string
  capabilities: Array<string>
  licensedBillableResourceCount: number
  currentBillableResourceCount: number
}

export function adaptEntitlementDto(obj: any): EntitlementDto {
  // Hack to enable testing of Spot
  // obj.capabilities.push('spotByNetApp');
  return {
    expiration: new Date(obj.expiration),
    productName: obj.productName,
    capabilities: obj.capabilities,
    licensedBillableResourceCount: obj.licensedBillableResourceCount,
    currentBillableResourceCount: obj.currentBillableResourceCount,
  }
}

export interface EntitlerDto {
  marketplace: string
  marketplaceUrl: string
}

export interface BaseRuleDto {
  ruleType: string
}

export interface TagDto {
  name: string
  value?: string
}
export interface AllDto {
  rules: TagDto[]
}

export interface AwsSourceResourceDto {
  type: ResourceType
  variant?: ResourceVariant
  arn: string
  parentArn?: string
  isContainer: boolean
  descendantCount: number
  id: string
  name: string
  displayName?: string
  tags: Array<TagDto>
  dependees: Array<string>
  weakDependees: Array<string>
  directDependees: Array<string>
  resourceGroup?: string
}

export function adaptAwsSourceResourceDto(obj: any) {
  let o = {
    type: obj.type,
    variant: obj.variant,
    arn: obj.arn,
    id: obj.id,
    name: obj.name,
    displayName: obj.displayName,
    parentArn: obj.parentArn,
    isContainer: obj.isContainer,
    descendantCount: obj.descendantCount,
    tags: obj.tags.map((tag: { name: string; value: string }) => {
      return tag as TagDto
    }),
    dependees: obj.dependees,
    weakDependees: obj.weakDependees || [],
    directDependees: obj.directDependees || [],
    resourceGroup: obj.resourceGroup || obj.resource_group || undefined,
  }

  return o as AwsSourceResourceDto
}

export interface ResourcePropertyDto {
  name: string
  value?: string
}

export interface AwsTargetResourceDto {
  sourceArn: string
  arn: string
  type: ResourceType
  variant?: ResourceVariant
  id: string
  name?: string
  displayName?: string
  resourceGroup?: string
  properties: Array<ResourcePropertyDto>
}

export interface ArnRuleDto extends BaseRuleDto {
  arns: Array<Arn>
  operator?: string
  action?: string
}

export interface ExcludeWeakArnRuleDto extends BaseRuleDto {
  arns: Array<Arn>
  operator?: string
}

export interface TagRuleDto extends BaseRuleDto {
  name: string
  value?: string
  operator?: string
  action?: string
}

export interface ResourceTypeRuleDto extends BaseRuleDto {
  resourceType: string
  operator?: string
  action?: string
}

export interface ResourceGroupRuleDto extends BaseRuleDto {
  resourceGroup: string
  operator?: string
  action?: string
}

export interface AllRuleDto extends BaseRuleDto {
  rules: BaseRuleDto[]
  action?: string
}

export enum NetworkSandboxService {
  AWS_S3 = 'aws:s3',
  AWS_DYNAMODB = 'aws:dynamodb',
}

export interface FailoverTestSyncConfigDto {
  enableNetworkSandbox: boolean
  advancedNetworkSandbox: boolean
  networkSandboxWhitelist?: string[]
  networkSandboxAllowedDomains?: string[]
  networkSandboxAllowedServices?: NetworkSandboxService[]
}

export interface RestoreConfigDto {
  enableQuarantine: boolean
}

export enum ApplicationType {
  AWS = 'aws',
  AZURE = 'azure',
}

export interface ApplicationDto {
  appId?: string
  accountId: string
  name: string
  type: ApplicationType
  rpo: number
  notificationEmails?: Array<string>
  sourceEndpointUri: string
  sourceAwsAccountId?: string
  sourceRegion?: string
  targetEndpointUri: string
  targetAwsAccountId?: string
  targetRegion?: string
  selectionRules: Array<BaseRuleDto>
  syncPhase: SyncPhaseDto
  failoverTestSyncConfig?: FailoverTestSyncConfigDto
  createdAt?: Date
  recoveryPointId?: string
  backupStatus?: ProcessingStatusDto
  restoreStatus?: ProcessingStatusDto
  failbackStatus?: ProcessingStatusDto
  restoreConfig?: RestoreConfigDto
  deleted?: boolean
  deletedAt?: Date
  restoreTrigger?: RestoreTriggerDto | null
  identityProviderId?: string
  lastRestoreJobId?: string
  resumeRecoveryStatus?: ResumeRecoveryStatusDto
}

export interface ResumeRecoveryStatusDto {
  available: boolean
  cause?: string
}

export interface ApplicationResourceStatusDto {
  timestamp: Date
  state: SyncPairResourceStateDto
  message?: string
  progress?: number
}

export interface ApplicationSourceResourceDto {
  id: string
  name?: string
  displayName?: string
  resourceGroup?: string
}

export interface ApplicationTargetResourceDto {
  arn: string
  id: string
  name?: string
  displayName?: string
  resourceGroup?: string
  //properties: List[AppResourcePropertyDto]
}

export interface ApplicationResourceDto {
  arn: string
  type: ResourceType
  variant?: ResourceVariant
  source?: ApplicationSourceResourceDto
  target?: ApplicationTargetResourceDto
  backupStatus?: ApplicationResourceStatusDto
  restoreStatus?: ApplicationResourceStatusDto
  failbackStatus?: ApplicationResourceStatusDto
}

export interface ApplicationResourcesResponseDto {
  resources: ApplicationResourceDto[]
  backupStatusNextToken?: string
  restoreStatusNextToken?: string
  failbackStatusNextToken?: string
}

export interface AppResourceStatusChangeDto {
  arn: string
  timestamp: Date
  state?: SyncPairResourceStateDto
  message?: string
  progress?: number
}

export interface AppResourceStatusChangesResponseDto {
  changes: AppResourceStatusChangeDto[]
  nextToken?: string
}

export enum RestoreTriggerDto {
  AUTOMATIC = 'automatic',
  MANUAL = 'manual',
}

export enum SyncPhaseDto {
  STANDBY = 'standby',
  FAILOVER_TEST = 'failoverTest',
  FAILOVER = 'failover',
}

export function adaptApplicationDto(appDtoObj): ApplicationDto {
  return {
    ...appDtoObj,
    failoverTestSyncConfig: appDtoObj.failoverTestSyncConfig as FailoverTestSyncConfigDto,
    createdAt: new Date(appDtoObj.createdAt),
    notificationEmails: appDtoObj.notificationEmails,
    recoveryPointId: appDtoObj.recoveryPointId,
    backupStatus: appDtoObj.backupStatus ? adaptProcessingStatusDto(appDtoObj.backupStatus) : null,
    restoreStatus: appDtoObj.restoreStatus ? adaptProcessingStatusDto(appDtoObj.restoreStatus) : null,
    failbackStatus: appDtoObj.failbackStatus ? adaptProcessingStatusDto(appDtoObj.failbackStatus) : null,
    deleted: appDtoObj.deleted,
    deletedAt: appDtoObj.deletedAt ? new Date(appDtoObj.deletedAt) : null,
    restoreTrigger: appDtoObj.restoreTrigger,
    restoreConfig: appDtoObj.restoreConfig as RestoreConfigDto,
  }
}

export function adaptApplicationResourceStatus(statusObj): ApplicationResourceStatusDto {
  return {
    ...statusObj,
    timestamp: new Date(statusObj.timestamp),
  }
}

export function adaptApplicationResource(resourceObj): ApplicationResourceDto {
  return {
    ...resourceObj,
    backupStatus: resourceObj.backupStatus ? adaptApplicationResourceStatus(resourceObj.backupStatus) : null,
    restoreStatus: resourceObj.restoreStatus ? adaptApplicationResourceStatus(resourceObj.restoreStatus) : null,
  }
}

export function adaptApplicationResourcesResponse(responseObj): ApplicationResourcesResponseDto {
  return {
    ...responseObj,
    resources: responseObj.resources.map(adaptApplicationResource),
  }
}

export function adaptAppResourceStatusChange(changeObj): AppResourceStatusChangeDto {
  return {
    ...changeObj,
    timestamp: new Date(changeObj.timestamp),
  }
}

export function adaptAppResourceStatusChangesResponse(responseObj): AppResourceStatusChangesResponseDto {
  return {
    ...responseObj,
    changes: responseObj.changes.map(adaptAppResourceStatusChange),
  }
}

export interface SyncPairInfo {
  accountId: string
  sourceEndpointUri: string
  targetEndpointUri: string
  identityProviderId?: string
}

export interface ProcessingStatusDto {
  active: boolean
  enabled: boolean
  lastFailure?: Date
  lastRequest?: Date
  lastStart?: Date
  lastSuccess?: Date
  lastJobId?: string
}

export interface SyncPairDto {
  sourceEndpointUri: string
  sourceAwsAccountId?: string
  sourceRegion: string
  targetEndpointUri: string
  targetAwsAccountId?: string
  targetRegion: string
  refreshAt?: Date
  recoveryPointId?: string
  stagingStatus?: ProcessingStatusDto
  syncStatus?: ProcessingStatusDto
  failbackStatus?: ProcessingStatusDto
}

export function adaptSyncPairDto(obj) {
  return {
    ...obj,
    refreshAt: obj.refreshAt ? new Date(obj.refreshAt) : null,
    recoveryPointId: obj.recoveryPointId,
    stagingStatus: adaptProcessingStatusDto(obj.stagingStatus),
    syncStatus: adaptProcessingStatusDto(obj.syncStatus),
    failbackStatus: adaptProcessingStatusDto(obj.failbackStatus),
  } as SyncPairDto
}

function adaptProcessingStatusDto(obj) {
  return {
    active: obj.active,
    enabled: obj.boolean,
    lastFailure: obj.lastFailure ? new Date(obj.lastFailure) : null,
    lastRequest: obj.lastRequest ? new Date(obj.lastRequest) : null,
    lastStart: obj.lastStart ? new Date(obj.lastStart) : null,
    lastSuccess: obj.lastSuccess ? new Date(obj.lastSuccess) : null,
    lastJobId: obj.lastJobId,
  } as ProcessingStatusDto
}

export interface SyncSourceDto {
  endpointUri: string
  awsAccountId?: string
  region?: string
  accessTemplateVersion: number
  scanStatus?: ProcessingStatusDto
  refreshStatus?: ProcessingStatusDto
}

export function adaptSyncSourceDto(obj) {
  return {
    ...obj,
    scanStatus: !!obj.scanStatus ? adaptProcessingStatusDto(obj.scanStatus) : null,
    refreshStatus: !!obj.refreshStatus ? adaptProcessingStatusDto(obj.refreshStatus) : null,
  } as SyncSourceDto
}

export interface ChangeDto {
  key: string
  description: string
}

export interface SyncPairAccessDto {
  sourceAccess: boolean
  sourceConfigValid: boolean
  sourceVersion: number
  sourceIsLatest: boolean
  sourceCloudFormationAccessStackName: string
  sourceTemplateManagedByStackSet?: boolean
  targetAccess: boolean
  targetConfigValid: boolean
  targetVersion: number
  targetIsLatest: boolean
  targetCloudFormationAccessStackName: string
  targetTemplateManagedByStackSet?: boolean
  failbackAccess: boolean
  failbackConfigValid: boolean
  failbackVersion: number
  failbackIsLatest: boolean
  failbackCloudFormationAccessStackName: string
  failbackTemplateManagedByStackSet?: boolean
  missingChanges?: ChangeDto[]
}

export interface AwsAccountDto {
  awsAccountId: string
  name?: string
}

export interface TemplateLocationDto {
  bucket: string
  key: string
}

export interface SyncPairTemplateUrlsDto {
  sourceTemplate: TemplateLocationDto
  targetTemplate: TemplateLocationDto
  failbackTemplate: TemplateLocationDto
}

export interface RegeneratedSyncPairDto {
  sourceEndpointUri: string
  targetEndpointUri: string
  templates: SyncPairTemplateUrlsDto
}

export interface AwsStackSetTemplate {
  sourceTemplate?: TemplateLocationDto
  targetTemplate?: TemplateLocationDto
  failbackTemplate?: TemplateLocationDto

  sourceStackSetTemplateS3Url?: string
  targetStackSetTemplateS3Url?: string
  failbackStackSetTemplateS3Url?: string

  // Other sync pairs that were regenerated when StackSets are enabled
  // (because access templates include authorized sources/targets for all sync pairs)
  regeneratedSyncPairs?: RegeneratedSyncPairDto[]
}

export interface AccessTemplatesDto {
  // Regular CloudFormation stacks (either presigned or not, depending on StackSets usage)
  sourceTemplateS3Url?: string
  targetTemplateS3Url?: string
  failbackTemplateS3Url?: string

  stackSetsTemplate?: AwsStackSetTemplate
  // StackSets wrapper templates (IAM-based URLs)
  sourceStackSetTemplateS3Url?: string
  targetStackSetTemplateS3Url?: string
  failbackStackSetTemplateS3Url?: string

  // Azure templates
  sourceInstallCommand?: string
  targetInstallCommand?: string
  failbackInstallCommand?: string
}

export interface ManualResourceTemplateDto {
  templateS3Url: string
}

export interface ErrorResponse {
  message: string
  authenticateUrl: string | null
}

export interface AccountSubjectDto {
  accountId?: string
  accountSubjectId?: string
  identityProviderId?: string
  email?: string
  verifyTokenIssuedAt?: Date
  verified?: boolean
  name?: string
  subjectId?: string
  roles?: string[] // Role IDs assigned to this user (for RBAC)
  lastLoginAt?: string // ISO date string of last login
  lastSsoRoles?: string[] // Role IDs from last SSO login
  lastSsoGroups?: string[] // Group IDs from last SSO login
}

export interface ApplicationRecoveryPointDto {
  applicationId: string
  applicationName?: string
  recoveryPointId: string
  timestamp: Date
  availableAt: Date
  protected: boolean
}

export function adaptAccountSubjectDto(obj) {
  return {
    accountId: !!obj.accountId ? obj.accountId : null,
    accountSubjectId: !!obj.accountSubjectId ? obj.accountSubjectId : null,
    identityProviderId: !!obj.identityProviderId ? obj.identityProviderId : null,
    email: !!obj.email ? obj.email : null,
    verifyTokenIssuedAt: !!obj.verifyTokenIssuedAt ? new Date(obj.verifyTokenIssuedAt) : null,
    verified: obj.verified !== undefined ? obj.verified : null,
    name: !!obj.name ? obj.name : null,
    subjectId: !!obj.subjectId ? obj.subjectId : null,
    roles: Array.isArray(obj.roles) ? obj.roles : null,
    lastLoginAt: !!obj.lastLoginAt ? obj.lastLoginAt : null,
    lastSsoRoles: Array.isArray(obj.lastSsoRoles) ? obj.lastSsoRoles : null,
    lastSsoGroups: Array.isArray(obj.lastSsoGroups) ? obj.lastSsoGroups : null,
  } as AccountSubjectDto
}

export function adaptApplicationRecoveryPointDto(rp) {
  return {
    ...rp,
    timestamp: new Date(rp.timestamp),
    availableAt: new Date(rp.availableAt),
  } as ApplicationRecoveryPointDto
}

export interface IdentityProviderConfigDto {
  type: string
  metadata?: string
  useGlobalSpEntityId?: boolean
  tenantId?: string
  consented?: boolean
  autoCreateSubjects?: boolean
}

export interface IdentityProviderDto {
  accountId?: string
  identityProviderId: string
  name: string
  type: string
  config: IdentityProviderConfigDto
}

export interface IntegrationConfigDto {
  type: string
}

export interface SpotByNetAppConfigDto extends IntegrationConfigDto {
  // type will be 'spotByNetApp'
  sourceAccountId: string
  sourceAuthToken?: string
  targetAccountId: string
  targetAuthToken?: string
}

export enum IntegrationTypeDto {
  SPOT_BY_NETAPP = 'spotByNetApp',
}

export interface IntegrationDto {
  integrationId: string
  accountId: string
  name: string
  type: IntegrationTypeDto
  config: IntegrationConfigDto
}

export interface PreviewInviteResponseDto {
  inviterEmail: string
}

export interface AcceptInviteRequestDto {
  inviteToken: string
  redirectUrl?: string
}

export interface VerifyEmailRequestDto {
  verifyToken: string
}

export interface StartPasswordResetRequestDto {
  email: string
}

export interface FinishPasswordResetRequestDto {
  passwordResetToken: string
  newPassword: string
}

export interface Issue {
  type: string
  message: string
  // Additional fields for derived classes, not enumerated.
  [prop: string]: any
}

export interface BaseIssueDto {
  issueId: string
  arn: Arn
  timestamp: Date
  issue: Issue
  resourceType?: ResourceType
}

export enum SyncPairIssueScopeDto {
  STAGING = 'staging',
  SYNC = 'sync',
  FAILBACK = 'failback',
  CLEANUP = 'cleanup',
}

export enum ApplicationIssueScopeDto {
  BACKUP = 'backup',
  RESTORE = 'restore',
  FAILBACK = 'failback',
  CLEANUP = 'cleanup',
  REFRESH = 'refresh',
  SCAN = 'scan',
  ENHANCE_FOR_RECOVERY_POINT = 'enhanceForRecoveryPoint',
}

export interface SyncPairIssueDto extends BaseIssueDto {
  scope: SyncPairIssueScopeDto
}

export interface ApplicationIssueDto extends BaseIssueDto {
  scope: ApplicationIssueScopeDto
}

function adaptBaseIssueDto(spIssueObj: any) {
  return {
    issueId: spIssueObj.issueId,
    arn: spIssueObj.arn,
    timestamp: new Date(spIssueObj.timestamp),
    issue: spIssueObj.issue,
    resourceType: spIssueObj.resourceType,
  } as BaseIssueDto
}

export function adaptSyncPairIssueDto(spIssueObj: any) {
  return {
    ...adaptBaseIssueDto(spIssueObj),
    scope: spIssueObj.scope,
  } as SyncPairIssueDto
}

export function adaptApplicationIssueDto(appIssueObj: any) {
  return {
    ...adaptBaseIssueDto(appIssueObj),
    scope: appIssueObj.scope,
  } as ApplicationIssueDto
}

export enum SyncSourceIssueScopeDto {
  REFRESH = 'refresh',
  ENHANCE_FOR_RECOVERY_POINT = 'enhanceForRecoveryPoint',
  SCAN = 'scan',
}

export interface SyncSourceIssueDto extends BaseIssueDto {
  scope: SyncSourceIssueScopeDto
}

export function adaptSyncSourceIssueDto(ssIssueObj: any) {
  return {
    ...adaptBaseIssueDto(ssIssueObj),
    scope: ssIssueObj.scope,
  } as SyncSourceIssueDto
}

export enum SyncPairResourceStateDto {
  INIT = 'init',
  PROCESSING = 'processing',
  UNSATISFIED_DEPENDENCY = 'unsatisfiedDependency',
  SUCCESS = 'success',
  FAILURE = 'failure',

  QUARANTINED = 'quarantined',
  SCAN_CLEAN = 'scan_clean',
  SCAN_INFECTED = 'scan_infected',
  SCAN_FAILED = 'scan_failed',
}

export enum SyncPairResourceStatusScopeDto {
  STAGING = 'staging',
  SYNC = 'sync',
  FAILBACK = 'failback',
}

export interface SyncPairResourceStatusDto {
  scope: SyncPairResourceStatusScopeDto
  arn: Arn
  timestamp: Date
  state: SyncPairResourceStateDto
  message?: string
  progress?: number
}

export function adaptSyncPairResourceStatusDto(spResourceStatusObj) {
  let adaptedDto = {
    scope: spResourceStatusObj.scope,
    arn: spResourceStatusObj.arn,
    timestamp: new Date(spResourceStatusObj.timestamp),
    state: spResourceStatusObj.state,
  } as SyncPairResourceStatusDto

  if (spResourceStatusObj.message !== undefined) {
    adaptedDto.message = spResourceStatusObj.message
  } else {
    adaptedDto.message = null
  }

  if (spResourceStatusObj.progress !== undefined) {
    adaptedDto.progress = spResourceStatusObj.progress
  }

  return adaptedDto
}

export interface RefreshSyncSourceDto {
  now: Date
}

export function adaptRefreshSyncSourceDto(rssDto) {
  return {
    now: new Date(rssDto.now),
  } as RefreshSyncSourceDto
}

export interface ScanSyncSourceRequestDto {
  extraIntegrationIds: [string]
  identityProviderId?: string
}

export interface ScanSyncSourceDto {
  now: Date
}

export function adaptScanSyncSourceDto(sssDto) {
  return {
    now: new Date(sssDto.now),
  } as ScanSyncSourceDto
}

export interface StageSyncPairDto {
  now: Date
}

export function adaptStageSyncPairDto(sspDtoObj) {
  return {
    now: new Date(sspDtoObj.now),
  } as StageSyncPairDto
}

export interface BackupRequestDto {
  appIds: Array<string>
}

export interface BackupResponseDto {
  apps: Array<ApplicationDto>
}

export function adaptBackupResponseDto(backupResponseDtoObj) {
  return {
    apps: backupResponseDtoObj.apps.map(adaptApplicationDto),
  } as BackupResponseDto
}

export interface ApplicationRestoreRequestDto {
  recoveryPointId?: string
  phase?: SyncPhaseDto
  enableRealTimeSync?: boolean
  enableNetworkSandbox?: boolean
  networkSandboxWhitelist?: string[]
  networkSandboxAllowedDomains?: string[]
  networkSandboxAllowedServices?: string[]
  enableQuarantine?: boolean
  enableMalwareScan?: boolean
  advancedNetworkSandbox?: boolean
  forceNewTargetPlan?: boolean
  protectRecoveryPoint?: boolean
}

export interface RestoreRequestDto {
  apps?: { [app_id: string]: ApplicationRestoreRequestDto }
  retryJobId?: string
}

export interface RestoreResponseDto {
  apps: Array<ApplicationDto>
}

export function adaptRestoreResponseDto(restoreResponseDtoObj) {
  return {
    apps: restoreResponseDtoObj.apps.map(adaptApplicationDto),
  } as RestoreResponseDto
}

export interface SyncSyncPairDto {
  now: Date
}

export function adaptSyncSyncPairDto(sspDtoObj) {
  return {
    now: new Date(sspDtoObj.now),
  } as SyncSyncPairDto
}

export interface SyncRequestDto {
  syncPhases: { [index: string]: SyncPhaseDto }
  recoveryPointId?: string
  enableRealTimeSync?: boolean
  enableNetworkSandbox?: boolean
  networkSandboxWhitelist?: string[]
  networkSandboxAllowedServices?: string[]
}

export interface FailoverTestResultDto {
  accountId: string
  timestamp: Date
  sourceEndpointUri: string
  sourceAwsAccountId: string
  sourceRegion: string
  targetEndpointUri: string
  targetAwsAccountId: string
  targetRegion: string
  appId: string
  success: boolean
  notes?: string
}

export function adaptFailoverTestResultDto(resultDto) {
  return {
    accountId: resultDto.accountId,
    timestamp: new Date(resultDto.timestamp),
    sourceEndpointUri: resultDto.sourceEndpointUri,
    sourceAwsAccountId: resultDto.sourceAwsAccountId,
    sourceRegion: resultDto.sourceRegion,
    targetEndpointUri: resultDto.targetEndpointUri,
    targetAwsAccountId: resultDto.targetAwsAccountId,
    targetRegion: resultDto.targetRegion,
    appId: resultDto.appId,
    success: resultDto.success,
    notes: !!resultDto.notes ? resultDto.notes : '',
  } as FailoverTestResultDto
}

export interface RecoveryPointDto {
  recoveryPointId: string
  timestamp: Date
  availableAt: Date
  protected: boolean
}

export function adaptRecoveryPointDto(rpDto) {
  return {
    recoveryPointId: rpDto.recoveryPointId,
    timestamp: new Date(rpDto.timestamp),
    availableAt: new Date(rpDto.availableAt),
    protected: rpDto.protected,
  } as RecoveryPointDto
}

export interface RecoveryPointAttemptDto {
  timestamp: Date
  createSuccess?: Date
  createFailure?: Date
  recoveryPointId: string
  maxReplicationLatency: number
}

export function adaptRecoveryPointAttemptDto(rpaDto) {
  return {
    timestamp: new Date(rpaDto.timestamp),
    createSuccess: !!rpaDto.createSuccess ? new Date(rpaDto.createSuccess) : null,
    createFailure: !!rpaDto.createFailure ? new Date(rpaDto.createFailure) : null,
    recoveryPointId: rpaDto.recoveryPointId,
    maxReplicationLatency: rpaDto.maxReplicationLatency,
  } as RecoveryPointAttemptDto
}

export interface ApplicationIntegrationDto {
  appId: string
  integrationId: string
}

export interface AuthenticatedSubjectDto {
  identityProviderType: string
  identityProviderAuthority: string
  subjectId: string
  effectiveAccountSubjectIds: Array<string>
}

export enum AdminRoleDto {
  READ_ONLY = 'readOnly',
  ACCOUNT_MANAGER = 'accountManager',
  FULL = 'full',
}

export interface AuthenticatedPrincipalDto {
  principalType: string // 'accountSubject' | 'apiKey'
  identityProviderType?: string
  identityProviderAuthority?: string
  identityProviderCerts?: string[] | null
  subjectId?: string
  effectiveAccountSubjectIds?: string[]
  expiresAt?: string
  effectivePermissions: {
    accounts: { [accountId: string]: string[] }
    apps: { [appId: string]: string[] }
  }
  adminRole?: AdminRoleDto
  key?: string // For API key principals
}

export interface RoleWithPoliciesDto {
  name: string
  createdAt: string
  isSystemRole: boolean
  externalId?: string
  accountId: string
  roleId: string
  policies: RolePolicyDto[]
  lastUpdatedAt: string
}

export interface RetentionSettingsDto {
  recentRetained: number
  daysRetained: number
  weeksRetained: number
  monthsRetained: number
  quartersRetained: number
}

export interface ResourceAttributesDto {
  tags: {
    [key: string]: string
  }
}

export interface ResourceConfigDto {
  resourceConfigType: string
  // other fields for derived types not enumerated
  [prop: string]: any
}

export interface SyncPairResourceConfigDto {
  arn: string
  substituteArn?: string
  resourceConfig?: ResourceConfigDto
}

export interface SyncPairResourceConfigsDto {
  configs: SyncPairResourceConfigDto[]
}

export interface ResourceTypeConfigDto {
  resourceType: string
  // other fields for derived types not enumerated
  [prop: string]: any
}

export interface AccountResourceTypeConfigsDto {
  configs: { [resourceType: string]: ResourceTypeConfigDto }
}

export interface FailbackRequestDto {
  appIds: [string]
}

export interface FailbackSyncPairDto {
  now: string
}

export interface ApplicationFailbackRequestDto {
  // Currently nothing in this DTO, but it's stubbed out on the server so providing it here as well
}

export interface MultiAccountFailbackRequestDto {
  // this is called FailbackRequestDto on the server, but that name is taken here
  apps?: {
    [appId: string]: ApplicationFailbackRequestDto
  }
}

export interface MultiAccountFailbackResponseDto {
  // this is called FailbackResponseDto on the server, but adding MultiAccount for symmetry with the request DTO above
  apps: Array<ApplicationDto>
}

export interface BlockedEndpointsResponseDto {
  status: string
  results: {
    endpoint: string
    count: number
  }[]
}

export interface UpdateApplicationRecoveryPointRequestDto {
  protected: boolean
}

export interface AwsRegionDto {
  partition: string
  partitionName: string
  region: string
  regionName: string
}

export interface AzureRegionDto {
  regionName: string
  region: string
  pairedRegion: string[]
  supportsAvailabilityZones: boolean
}

export interface RegionsResponseDto {
  aws: AwsRegionDto[]
  azure: AzureRegionDto[]
}

export interface RoleDto {
  roleId?: string
  accountId?: string
  name?: string
  externalId?: string
  policies?: RolePolicyDto[]
  isSystemRole?: boolean
  createdAt?: Date
  lastUpdatedAt?: Date
}

export interface RolePolicyDto {
  accessLevel: string
  allApps?: boolean | null
  appIds?: string[] | null
  appSetIds?: string[] | null
}

export interface AccountSubjectGroupDto {
  accountSubjectGroupId?: string
  accountId?: string
  name?: string
  externalId?: string
  accountSubjects?: string[]
  roles?: string[]
  createdAt?: Date
  lastUpdatedAt?: Date
}

export interface ApiKeyDto {
  apiKeyId?: string
  secret?: string
  name?: string
  enabled?: boolean
  createdAt?: Date
  lastUpdatedAt?: Date
  roles?: string[]
}

export interface AzureMarketplaceSubscriptionDto {
  subscriptionId: string
  subscriptionName: string
  status: string
  planDescription: string
  purchaserName: string
  purchaserEmail: string
  tenantId: string
  tenantName: string
}
